<template>
  <router-link v-if="href" :to="href" :class="className" @click="$emit('click')">
    <slot></slot>
  </router-link>
  <button v-else :class="className" @click="$emit('click')">
    <slot></slot>
  </button>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: function(value) {
        return (
          [
            'primary',
            'secondary',
            'tertiary',
            'opaque',
            'white',
            'filter',
            'twitter',
            'facebook',
            'linkedin',
            'google',
            'email'
          ].indexOf(value) !== -1
        )
      }
    },
    width: {
      type: String,
      default: 'medium',
      validator: function(value) {
        return (
          ['xsmall', 'small', 'medium', 'large', 'full', 'icon'].indexOf(
            value
          ) !== -1
        )
      }
    },
    rounded: {
      type: String,
      default: null,
      validator: function(value) {
        return ['small', 'full'].indexOf(value) !== -1
      }
    },
    href: {
      type: String,
      default: ''
    },
    bold: {
      type: Boolean,
      default: false
    },
    outline: {
      type: Boolean,
      default: false
    },
    flex: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    className() {
      return `btn ${(this.flex && 'btn--flex') || ''} btn--${
        this.variant
      } ${(this.rounded && 'btn--rounded-' + this.rounded) || ''} ${(this
        .outline &&
        'btn--outline') ||
        'btn--solid'}  btn--width-${this.width} ${(this.bold &&
        'btn--font-semibold') ||
        ''}`
    }
  }
}
</script>

<style lang="postcss">
.btn {
  @apply font-sans rounded-sm tracking-wide inline-block py-4 no-underline leading-tight;
}
.btn:focus,
.btn:active {
  @apply outline-none;
}

.btn--flex {
  @apply flex-1;
}

/* Text */
.btn--font-semibold {
  @apply font-semibold;
}

/* Rounding corners */
.btn--rounded-full {
  @apply rounded-full;
}
.btn--rounded-small {
  @apply rounded-sm;
}

/* Rounded + Width */
.btn--rounded-full.btn--width-small {
  @apply p-0 w-8 h-8 flex items-center justify-center;
}
.btn--rounded-full.btn--width-medium {
  @apply p-0 w-10 h-10 flex items-center justify-center;
}

/* Changes the padding width on the sides of the button */
.btn--width-icon {
  @apply p-2;
}
.btn--width-small {
  @apply px-6;
}
.btn--width-medium {
  @apply px-12;
}
.btn--width-large {
  @apply px-16;
}
.btn--width-full {
  @apply flex w-full justify-center;
}

/* Main Variants */
.btn--primary.btn--solid {
  @apply bg-secondary text-white;
}
.btn--primary.btn--solid:hover {
  @apply bg-secondary-dark;
}

.btn--secondary.btn--solid {
  @apply bg-primary-lightest text-primary-dark;
}
.btn--secondary.btn--solid:hover {
  @apply bg-primary text-white;
}
.btn--secondary.btn--solid.active {
  @apply bg-primary text-white;
}

.btn--tertiary.btn--solid {
  @apply bg-tertiary text-body;
}
.btn--tertiary.btn--solid:hover {
  @apply bg-tertiary-dark;
}
.btn--opaque.btn--solid {
  @apply bg-white-10 text-white;
}

.btn--white.btn--solid {
  @apply bg-white;
}

/* Outline Variants */
.btn--primary.btn--outline {
  @apply border border-secondary text-secondary;
}
.btn--primary.btn--outline:hover {
  @apply bg-secondary-dark;
}

.btn--secondary.btn--outline {
  @apply border border-primary-light text-primary-light;
}
.btn--secondary.btn--outline:hover {
  @apply bg-primary-lighter;
}

.btn--tertiary.btn--outline {
  @apply border border-tertiary text-tertiary;
}
.btn--tertiary.btn--outline:hover {
  @apply bg-tertiary;
}

/* Filter Variants */
.btn--filter.btn--solid {
  @apply bg-primary-lighter text-secondary;
}
.btn--filter.btn--solid:hover {
  @apply bg-primary-lightest;
}
.btn--filter.btn--solid.active {
  @apply bg-secondary text-white;
}
.btn--twitter.btn--solid {
  @apply bg-twitter text-white;
}

/* Social Variants */
.btn--facebook.btn--solid {
  @apply bg-facebook text-white;
}
.btn--linkedin.btn--solid {
  @apply bg-linkedin text-white;
}
.btn--google.btn--solid {
  @apply bg-google text-white;
}
.btn--email.btn--solid {
  @apply bg-secondary text-white;
}
.btn--twitter.btn--solid:hover {
  @apply text-twitter bg-primary-lightest;
}
.btn--facebook.btn--solid:hover {
  @apply text-facebook bg-primary-lightest;
}
.btn--linkedin.btn--solid:hover {
  @apply text-linkedin bg-primary-lightest;
}
.btn--google.btn--solid:hover {
  @apply text-google bg-primary-lightest;
}
.btn--email.btn--solid:hover {
  @apply text-secondary bg-primary-lightest;
}

@screen md {
  .btn--rounded-full.btn--width-small {
    @apply w-10 h-10;
  }
  .btn--rounded-full.btn--width-medium {
    @apply w-12 h-12;
  }
  .btn--width-xsmall {
    @apply px-6;
  }
}
</style>